import {source} from "azure-maps-control"
import {ILayerView, LayerInfo} from "@esWebAPI/es/model/Layer";
import {ColorThematic, SizeThematic, SymbolThematic} from "@esWebAPI/es/model/Thematic";
import {ExpressionFilterSpecification} from "@maplibre/maplibre-gl-style-spec";
import {NavUtils} from "@esWebAPI/es/util/NavUtils";

// note: keep these values in sync with server-side equivalent in LayerViewStyler.java
export const LAYER_DATA_LIMITS = {
    // Was 3m but these are ~75mb single GeoJSON blobs which can crash the browser. 
    MAX_VERTEX_FOR_ENTIRE_GEOJSON_LOAD: 1500000, // 1.5m
    MAX_FEATS_FOR_ENTIRE_GEOJSON_LOAD: 100000, // 100k
    MAX_FEATURE_RESULTS_FROM_A_CLICK: 200,
    MIN_BUBBLE_SIZE_FOR_OUTLINE: 5,
    MIN_FEAT_COUNT_FOR_OUTLINE: 25000, // 25k
}

export type PositionAnchor = "center" | "top" | "bottom" | "left" | "right" | "top-left" | "top-right" | "bottom-left" | "bottom-right";

export interface AzureLayer {

    create(datasource: source.Source, colorThematic?: ColorThematic, sizeThematic?: SizeThematic, symbolThematic?: SymbolThematic): Promise<void>;

    update(layerView: ILayerView, layerInfo: LayerInfo): Promise<void>;

    remove(): void;

    hide(): void;

    hideFeature(featureId: number): void;

    showFeatures(): void;

    getLayerId(): string;
}

/**
 * Returns true if either:
 *  - heatmap is disabled
 *  - heatmap is enabled AND showFeatures ("overlay pins") is enabled
 */
export const isHeatMapDisabledXorIsOverlayPinEnabled = (layerView: ILayerView): boolean => {
    const heatMapSettings = layerView.styleSettings?.heatMapSettings;

    if (!heatMapSettings || !heatMapSettings.enabled) {
        return true;
    }

    return heatMapSettings.showFeatures === true;
};

export const isPieClusteringOn = (layerView: ILayerView): boolean => {
    const rs = !!(
        layerView?.styleSettings.defaultStyle?.clustering ||
        layerView?.styleSettings.defaultStyle?.symbolStyle?.clustering
    );
    //es.log.log("isPieClusteringOn", rs, layerView?.styleSettings.defaultStyle);
    return rs;
};

export const isMouseoverLabellingEnabled = (layerView: ILayerView): boolean => {
    return !es.isNativeApp && !es.isTerritoryComparePage && (
            layerView.styleSettings?.defaultStyle?.mouseover ||
            layerView.styleSettings?.defaultStyle?.symbolStyle?.mouseover
    );
};

export const isBubbleLayer = (layerView: ILayerView, layerInfo: LayerInfo): boolean => {
    const isAboveMaxFeatsForSymbolLayer = layerInfo.featureInfo.count > LAYER_DATA_LIMITS.MAX_FEATS_FOR_ENTIRE_GEOJSON_LOAD;

    const defaultStyle = layerView.styleSettings.defaultStyle.$type === "SymbolAndPolygonStyle" ?
        layerView.styleSettings.defaultStyle.symbolStyle :
        layerView.styleSettings.defaultStyle;

    const isCircleSymbolStyleOverride =
        defaultStyle.symbolTypeId === 0 && !layerView.styleSettings?.symbolThematicSettings?.enabled;

    return isAboveMaxFeatsForSymbolLayer || isCircleSymbolStyleOverride;
};

export const getLayerFilter = (layerView: ILayerView, layerFilter?: ExpressionFilterSpecification, hideCluster: boolean = false): ExpressionFilterSpecification|undefined => {
    let filter: ExpressionFilterSpecification|undefined = undefined;
    if (isPieClusteringOn(layerView) || hideCluster) {
        //filter to only render points without a point_count (not in a cluster)
        filter = ['!', ['has', 'point_count']];
    }
    if (layerFilter) {
        if (filter) {
            filter = ['all', filter, layerFilter];
        } else {
            filter =  layerFilter;
        }
    }
    return filter;
};

export const getMinAndMaxZoomOpts = (layerView: ILayerView) => {
    const opts: {minZoom?: number, maxZoom?: number} = {};
    if(layerView.maxResolution){
        opts.minZoom = NavUtils.getZoomForResolution(layerView.maxResolution);
    }
    if(layerView.minResolution){
        opts.maxZoom = NavUtils.getZoomForResolution(layerView.minResolution);
    }
    return opts;
};

export const isLabelBoxEnabled = (layerView: ILayerView) => {
    return !!layerView.styleSettings.defaultStyle.text && layerView.styleSettings.defaultStyle.text.boxFillColor != undefined;
};
